import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { ShirtIcon, Package, History, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export const EmployeeVetements = ({ employeeId, employeeNom }) => {
  const [loading, setLoading] = useState(true);
  const [quotas, setQuotas] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [taillePreferee, setTaillePreferee] = useState("M");
  const [savingTaille, setSavingTaille] = useState(false);

  useEffect(() => {
    loadData();
  }, [employeeId, annee]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [quotasRes, empRes, distRes] = await Promise.all([
        axios.get(`${api.baseUrl}/vetements/quotas/${employeeId}?annee=${annee}`),
        axios.get(`${api.baseUrl}/employees/${employeeId}`),
        axios.get(`${api.baseUrl}/vetements/distributions?employee_id=${employeeId}&limit=50`)
      ]);

      setQuotas(quotasRes.data.quotas || []);
      setTaillePreferee(empRes.data.taille_vetements || "M");
      setDistributions(distRes.data.distributions || []);
    } catch (error) {
      console.error("Erreur chargement vêtements:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveTaille = async (nouvelleT) => {
    setSavingTaille(true);
    try {
      await axios.put(`${api.baseUrl}/employees/${employeeId}`, { taille_vetements: nouvelleT });
      setTaillePreferee(nouvelleT);
      toast.success(`Taille mise à jour : ${nouvelleT}`);
    } catch {
      toast.error("Erreur sauvegarde de la taille");
    } finally {
      setSavingTaille(false);
    }
  };

  const initQuotas = async (forceResync = false) => {
    try {
      const response = await axios.post(`${api.baseUrl}/vetements/quotas/init`, {
        employee_id: employeeId,
        annee: annee,
        force_resync: forceResync
      });
      if (response.status === 200) {
        const { quotas_created, permanence_cat } = response.data;
        toast.success(forceResync
          ? `Quotas resynchronisés (${permanence_cat}) — ${quotas_created} article(s) mis à jour`
          : `Quotas initialisés (${permanence_cat}) — ${quotas_created} article(s)`
        );
        loadData();
      }
    } catch (error) {
      console.error("Erreur init quotas:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'initialisation");
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Chargement...</div>;
  }

  return (
    <div className="space-y-5">

      {/* En-tête + boutons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShirtIcon className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-lg">Vêtements & Uniformes</h3>
          <Badge variant="outline">{annee}</Badge>
        </div>
        <div className="flex gap-2">
          {quotas.length > 0 && (
            <Button onClick={() => initQuotas(true)} size="sm" variant="outline" className="text-xs border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="btn-resync-politique">
              Resync Politique
            </Button>
          )}
          {quotas.length === 0 && (
            <Button onClick={() => initQuotas(false)} size="sm" className="bg-blue-600" data-testid="btn-init-quotas">
              Initialiser Quotas {annee}
            </Button>
          )}
        </div>
      </div>

      {/* Taille préférée */}
      <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-3 border border-slate-200">
        <ShirtIcon className="w-4 h-4 text-slate-500 shrink-0" />
        <span className="text-sm font-medium text-slate-700">Taille préférée :</span>
        <Select value={taillePreferee} onValueChange={saveTaille} disabled={savingTaille}>
          <SelectTrigger className="w-24 h-8 text-sm" data-testid="select-taille-preferee">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["XS","S","M","L","XL","XXL","3XL"].map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {savingTaille && <span className="text-xs text-slate-400">Sauvegarde...</span>}
        <span className="text-xs text-slate-400 ml-1">— utilisée lors de l'initialisation des quotas</span>
      </div>

      {/* Quotas par article (lecture seule) */}
      {quotas.length === 0 ? (
        <Card className="bg-slate-50">
          <CardContent className="pt-6 text-center text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p>Aucun quota configuré pour {annee}</p>
            <p className="text-sm mt-1">Cliquez sur "Initialiser Quotas" pour commencer</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quotas.map((quota) => {
            const pris = quota.quantite_prise || 0;
            const total = quota.quota_annuel || 0;
            const disponible = quota.disponible ?? (total - pris);
            const pct = total > 0 ? (pris / total) * 100 : 0;

            return (
              <Card key={`${quota.vetement_id}_${quota.taille}`}>
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-slate-900">{quota.vetement_nom}</p>
                      <p className="text-sm text-slate-500">Taille : {quota.taille}</p>
                    </div>
                    <Badge variant={disponible > 0 ? "default" : "destructive"} data-testid="badge-dispo-vetement">
                      {disponible > 0 ? `${disponible} disponible${disponible > 1 ? 's' : ''}` : 'Épuisé'}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 mb-1 font-medium">
                    <span>{pris} pris / {total} permis</span>
                    <span className="text-emerald-600">{disponible} restant{disponible > 1 ? 's' : ''}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${pct >= 100 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Historique des distributions (depuis Entrepôt) */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-sm text-slate-700">Historique des remises</span>
            {distributions.length > 0 && <Badge variant="outline" className="text-xs">{distributions.length}</Badge>}
          </div>
          <a href="/entrepot" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-3 h-3" /> Gérer dans Entrepôt
          </a>
        </div>

        {distributions.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">
            Aucune remise enregistrée pour cet employé
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {distributions.map((d, i) => (
              <div key={d.id || i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-800">{d.vetement_nom}</p>
                  <p className="text-xs text-slate-500">
                    Taille {d.taille} · {d.date_distribution ? new Date(d.date_distribution).toLocaleDateString('fr-CA') : '—'}
                    {d.approuve_par && ` · par ${d.approuve_par}`}
                  </p>
                </div>
                <Badge className="bg-blue-50 text-blue-700 text-xs">+{d.quantite}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};
